import { forwardRef, memo, useMemo } from "react";
import type { HTMLAttributes } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

export type VelocityTargetStatus = "OnTarget" | "BelowTarget" | "AboveTarget" | "Inconsistent" | "Exceeded";

const StyledChip = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: theme.spacing(0.25, 1),
  borderRadius: theme.spacing(0.5),
  whiteSpace: "nowrap",
  cursor: "default",
  ...theme.typography.TextM,
  fontWeight: theme.typography.fontWeightMedium,

  "&[data-status='OnTarget']": {
    color: theme.palette.secondary1[180],
    backgroundColor: theme.palette.secondary1[10],
  },

  "&[data-status='BelowTarget'], &[data-status='Exceeded']": {
    color: theme.palette.secondary2[100],
    backgroundColor: theme.palette.secondary2[10],
  },

  "&[data-status='AboveTarget']": {
    color: theme.palette.secondary3[160],
    backgroundColor: theme.palette.secondary3[20],
  },

  "&[data-status='Inconsistent']": {
    color: theme.palette.grey[120],
    backgroundColor: theme.palette.grey[20],
  },
}));

interface VelocityTargetStatusChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  status: VelocityTargetStatus;
}

/**
 * @description Feedback chip for how a set (or a whole exercise) performed against its velocity target:
 * on target, below target, above target, inconsistent or exceeded.
 */
const VelocityTargetStatusChip = forwardRef<HTMLSpanElement, VelocityTargetStatusChipProps>((props, ref) => {
  const { status, ...rest } = props;
  const { t } = useTranslation();

  const statusLabels: Record<VelocityTargetStatus, string> = useMemo(
    () => ({
      OnTarget: t("workouts.velocity_target_status_on_target"),
      BelowTarget: t("workouts.velocity_target_status_below_target"),
      AboveTarget: t("workouts.velocity_target_status_above_target"),
      Inconsistent: t("workouts.velocity_target_status_inconsistent"),
      Exceeded: t("workouts.velocity_target_status_exceeded"),
    }),
    [t]
  );

  return (
    <StyledChip ref={ref} {...rest} data-status={status}>
      {statusLabels[status]}
    </StyledChip>
  );
});

export default memo(VelocityTargetStatusChip);
